/**
 * BETA5 Bespoke Bundle Builder v3
 * Core Logic: bespoke-builder-v3.js
 *
 * Architecture: Single product with 4 variants (4pc, 6pc, 12pc, 24pc).
 * Flavors from the "loose-chocolates" collection as real Shopify products.
 * Two-column layout with sticky sidebar. Mobile bottom bar + slide-up panel.
 */

(function () {
  'use strict';

  /* ============================
     CONFIG & STATE
     ============================ */

  const config = window.bespokeBuilder;
  if (!config || !config.variants || !config.chocolates) {
    console.warn('[Beta5 Builder] Missing configuration. Exiting.');
    return;
  }

  // Determine active variant (box size)
  const urlParams = new URLSearchParams(window.location.search);
  const urlSize = urlParams.get('size') ? parseInt(urlParams.get('size'), 10) : null;

  let activeVariant = config.variants.find(v => v.size === (urlSize || config.defaultSize))
                    || config.variants[0];

  let bundleSize = activeVariant.size;

  // Selection state: { productId (string): quantity (number) }
  const selected = {};

  // Chocolates lookup
  const chocolates = config.chocolates;

  // Inventory refresh interval
  const REFRESH_INTERVAL = 60000; // 60 seconds

  /* ============================
     DOM REFERENCES
     ============================ */

  const dom = {};

  function cacheDom() {
    dom.grid = document.getElementById('chocolate-grid');
    dom.sidebar = document.getElementById('bb-sidebar');
    dom.sizeOptions = document.getElementById('size-options');
    dom.selectedCount = document.getElementById('bb-selected-count');
    dom.boxSizeLabel = document.getElementById('bb-box-size');
    dom.progressFill = document.getElementById('bb-progress-fill');
    dom.smartMsg = document.getElementById('bb-smart-msg');
    dom.selectionsEmpty = document.getElementById('bb-selections-empty');
    dom.selectionsList = document.getElementById('bb-selections-list');
    dom.addToCart = document.getElementById('bb-add-to-cart');
    dom.cartPrice = document.getElementById('bb-cart-price');
    dom.toast = document.getElementById('bb-toast');
    dom.emptyFilter = document.getElementById('empty-filter-msg');
    dom.subSelect = document.getElementById('bb-substitution-select');

    // Mobile
    dom.mobileBar = document.getElementById('bb-mobile-bar');
    dom.mobileCount = document.getElementById('bb-mobile-count');
    dom.mobileSize = document.getElementById('bb-mobile-size');
    dom.mobileMsg = document.getElementById('bb-mobile-msg');
    dom.mobileBarSize = document.getElementById('bb-mobile-bar-size');
    dom.mobileSizesTop = document.getElementById('bb-mobile-sizes-top');
    dom.mobileCart = document.getElementById('bb-mobile-cart');
    dom.mobilePanel = document.getElementById('bb-mobile-panel');
    dom.mobilePanelClose = document.getElementById('bb-mobile-panel-close');
    dom.mobileSizes = document.getElementById('bb-mobile-sizes');
    dom.mobileSelections = document.getElementById('bb-mobile-selections');
    dom.mobileSubSelect = document.getElementById('bb-mobile-sub-select');
    dom.mobilePanelCart = document.getElementById('bb-mobile-panel-cart');
    dom.mobilePanelPrice = document.getElementById('bb-mobile-panel-price');
  }

  /* ============================
     HELPERS
     ============================ */

  function getChocolate(pid) {
    return chocolates.find(c => String(c.id) === String(pid));
  }

  function getTotalSelected() {
    return Object.values(selected).reduce((s, q) => s + q, 0);
  }

  function showToast(msg, duration) {
    duration = duration || 3000;
    dom.toast.textContent = msg;
    dom.toast.classList.add('visible');
    clearTimeout(dom.toast._timer);
    dom.toast._timer = setTimeout(() => dom.toast.classList.remove('visible'), duration);
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(0);
  }

  /* ============================
     SIZE SWITCHING
     ============================ */

  function setActiveSize(variantId) {
    const variant = config.variants.find(v => v.id === variantId);
    if (!variant) return;

    activeVariant = variant;
    bundleSize = variant.size;

    // If selections exceed new size, trim from the end
    let total = getTotalSelected();
    if (total > bundleSize) {
      // Remove items starting from last added
      const entries = Object.entries(selected);
      for (let i = entries.length - 1; i >= 0 && total > bundleSize; i--) {
        const [pid, qty] = entries[i];
        const excess = total - bundleSize;
        if (qty <= excess) {
          delete selected[pid];
          total -= qty;
        } else {
          selected[pid] = qty - excess;
          total -= excess;
        }
      }
      showToast('Some selections were removed to fit the ' + bundleSize + '-piece box.');
    }

    // Update all size buttons (desktop sidebar + mobile panel)
    document.querySelectorAll('.bb-size-btn').forEach(btn => {
      btn.classList.toggle('bb-size-btn--active', parseInt(btn.dataset.size, 10) === bundleSize);
    });

    // Update mobile size pills below hero
    document.querySelectorAll('.bb-mobile-size-pill').forEach(pill => {
      pill.classList.toggle('bb-mobile-size-pill--active', parseInt(pill.dataset.size, 10) === bundleSize);
    });

    // Update price displays
    if (dom.cartPrice) dom.cartPrice.textContent = variant.priceFormatted;
    if (dom.mobilePanelPrice) dom.mobilePanelPrice.textContent = variant.priceFormatted;

    // Update bar size label
    if (dom.mobileBarSize) dom.mobileBarSize.textContent = bundleSize + 'pc · ' + variant.priceFormatted;

    // Update size labels
    if (dom.boxSizeLabel) dom.boxSizeLabel.textContent = bundleSize;
    if (dom.mobileSize) dom.mobileSize.textContent = bundleSize;

    // Update URL without reload
    const url = new URL(window.location);
    url.searchParams.set('size', bundleSize);
    window.history.replaceState({}, '', url);

    updateDisplay();
  }

  function initSizeSwitcher() {
    // Desktop size buttons
    if (dom.sizeOptions) {
      dom.sizeOptions.addEventListener('click', function (e) {
        const btn = e.target.closest('.bb-size-btn');
        if (!btn) return;
        setActiveSize(parseInt(btn.dataset.variantId, 10));
      });
    }

    // Populate mobile panel size buttons
    if (dom.mobileSizes) {
      config.variants.forEach(v => {
        const btn = document.createElement('button');
        btn.className = 'bb-size-btn' + (v.size === bundleSize ? ' bb-size-btn--active' : '');
        btn.dataset.variantId = v.id;
        btn.dataset.size = v.size;
        btn.innerHTML =
          '<span class="bb-size-btn__count">' + v.size + 'pc</span>' +
          '<span class="bb-size-btn__price">' + v.priceFormatted + '</span>';
        btn.type = 'button';
        btn.addEventListener('click', () => setActiveSize(v.id));
        dom.mobileSizes.appendChild(btn);
      });
    }
  }

  /* ============================
     INCREMENT / DECREMENT
     ============================ */

  function increment(pid) {
    const choc = getChocolate(pid);
    if (!choc) return;
    if (!choc.available) return;

    const total = getTotalSelected();
    if (total >= bundleSize) {
      showToast('Your box is full! Remove a flavour or switch to a larger size.');
      return;
    }

    const current = selected[pid] || 0;
    if (choc.inventoryPolicy === 'deny' && current >= choc.inventory) {
      showToast(choc.title + ' has limited stock (' + choc.inventory + ' remaining).');
      return;
    }

    selected[pid] = current + 1;
    updateDisplay();
    updateCardState(pid);
  }

  function decrement(pid) {
    if (!selected[pid]) return;
    selected[pid]--;
    if (selected[pid] <= 0) delete selected[pid];
    updateDisplay();
    updateCardState(pid);
  }

  function removeSelection(pid) {
    delete selected[pid];
    updateDisplay();
    updateCardState(pid);
  }

  /* ============================
     DISPLAY UPDATES
     ============================ */

  function updateDisplay() {
    const total = getTotalSelected();
    const remaining = bundleSize - total;
    const isFull = remaining === 0;

    // Progress
    if (dom.selectedCount) dom.selectedCount.textContent = total;
    if (dom.boxSizeLabel) dom.boxSizeLabel.textContent = bundleSize;
    if (dom.progressFill) {
      const pct = Math.min((total / bundleSize) * 100, 100);
      dom.progressFill.style.width = pct + '%';
      dom.progressFill.classList.toggle('bb-progress__fill--full', isFull);
    }

    // Smart message
    const msg = getSmartMessage(total, remaining, isFull);
    if (dom.smartMsg) dom.smartMsg.textContent = msg;

    // Sidebar selections
    updateSelectionsList(dom.selectionsList, dom.selectionsEmpty);

    // Mobile selections (in panel)
    if (dom.mobileSelections) {
      updateSelectionsList(dom.mobileSelections, null);
    }

    // Cart buttons
    updateCartButtons(isFull);

    // Mobile bar
    if (dom.mobileCount) dom.mobileCount.textContent = total;
    if (dom.mobileMsg) dom.mobileMsg.textContent = isFull ? 'Box complete!' : remaining + ' more to go';

    // Increment button states on all cards
    updateAllIncrementStates();

    // Card quantity badges
    updateAllCardBadges();
  }

  function getSmartMessage(total, remaining, isFull) {
    if (total === 0) return 'Start by choosing your first flavour below.';
    if (isFull) return "That's a stunning selection — let's make it official.";
    if (remaining === 1) return 'Just 1 more to go...';
    if (remaining <= Math.floor(bundleSize / 2)) return "You're almost there — just " + remaining + ' more to complete your box.';
    return "You're crafting something special — " + remaining + ' more to go.';
  }

  function updateSelectionsList(container, emptyEl) {
    if (!container) return;
    const entries = Object.entries(selected);

    if (emptyEl) {
      emptyEl.style.display = entries.length ? 'none' : '';
    }

    // Build new content
    const fragment = document.createDocumentFragment();
    entries.forEach(([pid, qty]) => {
      const choc = getChocolate(pid);
      if (!choc) return;

      const row = document.createElement('div');
      row.className = 'bb-sel-row';
      row.dataset.pid = pid;

      row.innerHTML =
        '<img class="bb-sel-row__img" src="' + (choc.image || '') + '" alt="' + choc.title + '" width="40" height="40">' +
        '<div class="bb-sel-row__info">' +
          '<div class="bb-sel-row__name">' + choc.title + '</div>' +
        '</div>' +
        '<div class="bb-sel-row__controls">' +
          '<button class="bb-sel-row__btn" data-action="dec" data-pid="' + pid + '" type="button" aria-label="Remove one">−</button>' +
          '<span class="bb-sel-row__count">' + qty + '</span>' +
          '<button class="bb-sel-row__btn" data-action="inc" data-pid="' + pid + '" type="button" aria-label="Add one">+</button>' +
        '</div>';

      fragment.appendChild(row);
    });

    container.innerHTML = '';
    container.appendChild(fragment);
  }

  function updateCartButtons(isFull) {
    // Desktop
    if (dom.addToCart) {
      dom.addToCart.disabled = !isFull;
      dom.addToCart.classList.toggle('bb-add-to-cart--ready', isFull);
    }
    // Mobile bar
    if (dom.mobileCart) {
      dom.mobileCart.disabled = !isFull;
      dom.mobileCart.classList.toggle('bb-mobile-bar__cart--ready', isFull);
    }
    // Mobile panel
    if (dom.mobilePanelCart) {
      dom.mobilePanelCart.disabled = !isFull;
      dom.mobilePanelCart.classList.toggle('bb-mobile-panel__cart-btn--ready', isFull);
    }
  }

  function updateCardState(pid) {
    const card = dom.grid.querySelector('[data-product-id="' + pid + '"]');
    if (!card) return;

    const qty = selected[pid] || 0;
    card.classList.toggle('bb-card--selected', qty > 0);

    // Quantity display on card
    const qtyEl = card.querySelector('[data-qty-for="' + pid + '"]');
    if (qtyEl) qtyEl.textContent = qty;

    // Update increment button state for this card
    updateIncrementState(card, pid);
  }

  function updateAllCardBadges() {
    dom.grid.querySelectorAll('.bb-card').forEach(card => {
      const pid = card.dataset.productId;
      const qty = selected[pid] || 0;
      card.classList.toggle('bb-card--selected', qty > 0);

      // Qty display
      const qtyEl = card.querySelector('[data-qty-for="' + pid + '"]');
      if (qtyEl) qtyEl.textContent = qty;

      // Qty badge on image
      let badge = card.querySelector('.bb-card__qty-badge');
      if (qty > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'bb-card__qty-badge';
          card.querySelector('.bb-card__img-wrap').appendChild(badge);
        }
        badge.textContent = qty;
      } else if (badge) {
        badge.remove();
      }
    });
  }

  function updateAllIncrementStates() {
    dom.grid.querySelectorAll('.bb-card').forEach(card => {
      updateIncrementState(card, card.dataset.productId);
    });
  }

  function updateIncrementState(card, pid) {
    const choc = getChocolate(pid);
    const plusBtn = card.querySelector('.bb-qty-btn--plus');
    const minusBtn = card.querySelector('.bb-qty-btn--minus');
    if (!plusBtn || !choc) return;

    const total = getTotalSelected();
    const qty = selected[pid] || 0;
    let disablePlus = false;

    if (!choc.available) disablePlus = true;
    if (total >= bundleSize && qty === 0) disablePlus = true;
    if (total >= bundleSize) disablePlus = true;
    if (choc.inventoryPolicy === 'deny' && qty >= choc.inventory) disablePlus = true;

    plusBtn.disabled = disablePlus;
    if (minusBtn) minusBtn.disabled = qty === 0;
  }

  /* ============================
     ALLERGEN FILTERS
     ============================ */

  const activeFilters = new Set();
  const VEGAN_EXCLUDES = ['dairy', 'egg', 'gelatine'];

  function initAllergenFilters() {
    document.querySelectorAll('.bb-pill').forEach(pill => {
      pill.addEventListener('click', function () {
        const allergen = this.dataset.allergen;
        if (activeFilters.has(allergen)) {
          activeFilters.delete(allergen);
          this.classList.remove('active');
        } else {
          activeFilters.add(allergen);
          this.classList.add('active');
        }
        applyFilters();
      });
    });
  }

  function applyFilters() {
    if (activeFilters.size === 0) {
      dom.grid.querySelectorAll('.bb-card').forEach(c => c.classList.remove('bb-card--hidden'));
      if (dom.emptyFilter) dom.emptyFilter.style.display = 'none';
      return;
    }

    let visibleCount = 0;

    dom.grid.querySelectorAll('.bb-card').forEach(card => {
      const allergens = (card.dataset.allergens || '').split(',').map(a => a.trim()).filter(Boolean);
      let hide = false;

      // Standard filters: hide if item CONTAINS the allergen
      activeFilters.forEach(filter => {
        if (filter === 'vegan') {
          // Vegan: hide if contains dairy, egg, or gelatine
          if (VEGAN_EXCLUDES.some(a => allergens.includes(a))) hide = true;
        } else {
          if (allergens.includes(filter)) hide = true;
        }
      });

      card.classList.toggle('bb-card--hidden', hide);
      if (!hide) visibleCount++;
    });

    if (dom.emptyFilter) {
      dom.emptyFilter.style.display = visibleCount === 0 ? '' : 'none';
    }
  }

  /* ============================
     CART SUBMISSION
     ============================ */

  let isSubmitting = false;

  async function handleSubmit() {
    if (isSubmitting) return;
    const total = getTotalSelected();
    if (total !== bundleSize) {
      showToast('Please select exactly ' + bundleSize + ' chocolates.');
      return;
    }

    isSubmitting = true;
    updateSubmitButtonState('Adding...');

    try {
      // Pre-submit inventory check
      const inventoryOk = await validateInventory();
      if (!inventoryOk) {
        isSubmitting = false;
        updateSubmitButtonState(null);
        return;
      }

      // Build payloads
      const bundleKey = Date.now().toString();
      const components = buildComponentsPayload();
      const bundleTypeKey = bundleSize + 'pc_box';
      const subRaw = (dom.subSelect && dom.subSelect.value) || 'replace';
      const subLabels = {
        'replace': "Chocolatier's Choice",
        'double': 'Double Up on Existing',
        'contact': 'Contact Me First'
      };
      const subPref = subLabels[subRaw] || subRaw;

      // Parent line item properties
      const parentProps = {
        '_bundle_key': bundleKey,
        '_bundle_parent': 'true',
        '_box_size': String(bundleSize),
        '_bundle_type': bundleTypeKey,
        '_components': JSON.stringify(components),
        'If Unavailable': subPref
      };

      // Dietary filter preferences
      if (activeFilters.size > 0) {
        const filterLabels = {
          'dairy': 'Dairy-free',
          'gluten': 'Gluten-free',
          'nut': 'Nut-free',
          'peanut': 'Peanut-free',
          'alcohol': 'Alcohol-free',
          'vegan': 'Vegan'
        };
        const labels = Array.from(activeFilters)
          .map(f => filterLabels[f] || f)
          .join(', ');
        parentProps['Dietary requirements'] = labels;
      }

      // Human-readable chocolate labels
      let chocoIndex = 1;
      Object.entries(selected).forEach(([pid, qty]) => {
        const choc = getChocolate(pid);
        if (!choc) return;
        for (let i = 0; i < qty; i++) {
          parentProps['_Chocolate ' + chocoIndex] = choc.title.toUpperCase();
          chocoIndex++;
        }
      });

      // POST single parent line item to cart
      // Cart Transform expand creates component entries at checkout for inventory decrement
      const response = await fetch(config.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: activeVariant.id,
            quantity: 1,
            properties: parentProps
          }]
        })
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.description || 'Failed to add to cart');
      }

      // Redirect to cart
      // (Bundle summary note is generated on the cart page by bundle-cart-note.js)
      window.location.href = config.cartUrl;

    } catch (err) {
      console.error('[Beta5 Builder] Submit error:', err);
      showToast('Something went wrong. Please try again.');
      isSubmitting = false;
      updateSubmitButtonState(null);
    }
  }

  function buildComponentsPayload() {
    const components = [];
    Object.entries(selected).forEach(([pid, qty]) => {
      const choc = getChocolate(pid);
      if (!choc) return;
      components.push({ id: choc.variantId, qty: qty });
    });
    return components;
  }

  function updateSubmitButtonState(text) {
    [dom.addToCart, dom.mobileCart, dom.mobilePanelCart].forEach(btn => {
      if (!btn) return;
      if (text) {
        btn.dataset.origText = btn.innerHTML;
        btn.innerHTML = text;
        btn.disabled = true;
      } else {
        if (btn.dataset.origText) btn.innerHTML = btn.dataset.origText;
        btn.disabled = getTotalSelected() !== bundleSize;
      }
    });
  }

  /* ============================
     INVENTORY REFRESH & VALIDATION
     ============================ */

  async function refreshInventory() {
    try {
      const url = window.location.pathname + '?section_id=bundle-inventory-api';
      const resp = await fetch(url);
      if (!resp.ok) return;
      const html = await resp.text();

      // Parse inventory data from the section response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const dataEl = doc.querySelector('[data-inventory-json]');
      if (!dataEl) return;

      const invData = JSON.parse(dataEl.getAttribute('data-inventory-json'));
      if (!Array.isArray(invData)) return;

      invData.forEach(item => {
        const choc = chocolates.find(c => String(c.id) === String(item.id));
        if (!choc) return;
        choc.inventory = item.inventory;
        choc.available = item.available;
      });

      // Update sold-out cards
      dom.grid.querySelectorAll('.bb-card').forEach(card => {
        const pid = card.dataset.productId;
        const choc = getChocolate(pid);
        if (!choc) return;
        card.classList.toggle('bb-card--sold-out', !choc.available);
      });

      updateAllIncrementStates();

    } catch (e) {
      // Silent fail on refresh
    }
  }

  async function validateInventory() {
    await refreshInventory();

    let valid = true;
    const corrections = [];

    Object.entries(selected).forEach(([pid, qty]) => {
      const choc = getChocolate(pid);
      if (!choc) return;

      if (!choc.available) {
        corrections.push({ pid, name: choc.title, was: qty, now: 0 });
        delete selected[pid];
        valid = false;
      } else if (choc.inventoryPolicy === 'deny' && qty > choc.inventory) {
        const newQty = Math.max(0, choc.inventory);
        corrections.push({ pid, name: choc.title, was: qty, now: newQty });
        if (newQty === 0) {
          delete selected[pid];
        } else {
          selected[pid] = newQty;
        }
        valid = false;
      }
    });

    if (!valid) {
      const names = corrections.map(c => c.name).join(', ');
      showToast('Some selections are no longer available: ' + names + '. Please adjust your box.', 5000);
      updateDisplay();
    }

    return valid;
  }

  /* ============================
     EVENT WIRING
     ============================ */

  function initEvents() {
    // Grid: event delegation for +/- buttons
    dom.grid.addEventListener('click', function (e) {
      const plusBtn = e.target.closest('.bb-qty-btn--plus');
      if (plusBtn) {
        e.preventDefault();
        increment(plusBtn.dataset.id);
        return;
      }
      const minusBtn = e.target.closest('.bb-qty-btn--minus');
      if (minusBtn) {
        e.preventDefault();
        decrement(minusBtn.dataset.id);
        return;
      }
    });

    // Sidebar selection controls (delegated)
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.bb-sel-row__btn');
      if (!btn) return;
      e.preventDefault();
      const pid = btn.dataset.pid;
      if (btn.dataset.action === 'inc') increment(pid);
      else if (btn.dataset.action === 'dec') decrement(pid);
    });

    // Add to cart
    if (dom.addToCart) dom.addToCart.addEventListener('click', handleSubmit);
    if (dom.mobileCart) dom.mobileCart.addEventListener('click', handleSubmit);
    if (dom.mobilePanelCart) dom.mobilePanelCart.addEventListener('click', handleSubmit);

    // Sync substitution dropdowns
    if (dom.subSelect && dom.mobileSubSelect) {
      dom.subSelect.addEventListener('change', function () {
        dom.mobileSubSelect.value = this.value;
      });
      dom.mobileSubSelect.addEventListener('change', function () {
        dom.subSelect.value = this.value;
      });
    }

    // Mobile size pills below hero
    if (dom.mobileSizesTop) {
      dom.mobileSizesTop.addEventListener('click', function (e) {
        const pill = e.target.closest('.bb-mobile-size-pill');
        if (!pill) return;
        setActiveSize(parseInt(pill.dataset.variantId, 10));
      });
    }

    // Mobile bar size label + count area → opens panel
    if (dom.mobileBarSize) {
      dom.mobileBarSize.addEventListener('click', openMobilePanel);
    }
    var mobileBarInfo = document.querySelector('.bb-mobile-bar__info');
    if (mobileBarInfo) {
      mobileBarInfo.style.cursor = 'pointer';
      mobileBarInfo.addEventListener('click', openMobilePanel);
    }
    if (dom.mobilePanelClose) {
      dom.mobilePanelClose.addEventListener('click', closeMobilePanel);
    }

    // Overlay click to close
    document.addEventListener('click', function (e) {
      if (e.target.classList.contains('bb-overlay')) closeMobilePanel();
    });
  }

  /* ============================
     MOBILE PANEL
     ============================ */

  let overlay = null;

  function openMobilePanel() {
    if (!dom.mobilePanel) return;

    // Create overlay
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'bb-overlay';
      document.body.appendChild(overlay);
    }

    // Update selections in panel
    updateSelectionsList(dom.mobileSelections, null);

    dom.mobilePanel.style.display = 'block';
    overlay.style.display = 'block';

    // Force reflow before animating
    dom.mobilePanel.offsetHeight;
    overlay.offsetHeight;

    dom.mobilePanel.classList.add('open');
    overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  }

  function closeMobilePanel() {
    if (!dom.mobilePanel) return;
    dom.mobilePanel.classList.remove('open');
    if (overlay) overlay.classList.remove('open');
    document.body.style.overflow = '';

    setTimeout(() => {
      dom.mobilePanel.style.display = '';
      if (overlay) overlay.style.display = '';
    }, 300);
  }

  /* ============================
     GUIDED FLOW BRIDGE
     ============================ */

  function applyRandomSelection(selectionObj) {
    // Clear current selections
    Object.keys(selected).forEach(k => delete selected[k]);
    // Apply new selections
    Object.entries(selectionObj).forEach(([pid, qty]) => {
      selected[pid] = qty;
    });
    updateDisplay();
    updateAllCardBadges();
    applyFilters(); // preserve any active filters on the grid
  }

  /* ============================
     DEBUG
     ============================ */

  window.debugBespokeBuilder = {
    get selected() { return { ...selected }; },
    get bundleSize() { return bundleSize; },
    get activeVariant() { return { ...activeVariant }; },
    chocolates: chocolates,
    refreshInventory: refreshInventory,
    setSize: setActiveSize,
    addFilter: function(allergen) {
      activeFilters.add(allergen);
      document.querySelectorAll('.bb-pill[data-allergen="' + allergen + '"]')
        .forEach(p => p.classList.add('active'));
      applyFilters();
    },
    clearFilters: function() {
      activeFilters.clear();
      document.querySelectorAll('.bb-pill').forEach(p => p.classList.remove('active'));
      applyFilters();
    },
    applyRandomSelection: applyRandomSelection,
    handleSubmit: handleSubmit
  };

  /* ============================
     INIT
     ============================ */

  function init() {
    cacheDom();
    if (!dom.grid) {
      console.warn('[Beta5 Builder] Grid not found. Exiting.');
      return;
    }

    initSizeSwitcher();
    initAllergenFilters();
    initEvents();
    initGridScroll();

    // Set initial state
    updateDisplay();

    // Start inventory refresh cycle
    setInterval(refreshInventory, REFRESH_INTERVAL);

    console.log('[Beta5 Builder] v3 initialized. Box size:', bundleSize);
  }

  // Desktop-only: make the grid scrollable within the viewport
  // and position sticky filters below the theme header
  function initGridScroll() {
    if (window.innerWidth < 1025) return;

    var filters = document.querySelector('.bb-filters');

    function setGridHeight() {
      if (window.innerWidth < 1025) {
        dom.grid.style.maxHeight = '';
        if (filters) filters.style.top = '';
        return;
      }

      // Measure the theme header by finding where our section starts
      var container = document.getElementById('bespoke-builder-v3');
      if (container) {
        var containerRect = container.getBoundingClientRect();
        var headerOffset = Math.max(0, containerRect.top);
        if (filters) filters.style.top = headerOffset + 'px';
      }

      var rect = dom.grid.getBoundingClientRect();
      var available = window.innerHeight - rect.top - 24;
      if (available > 300) {
        dom.grid.style.maxHeight = available + 'px';
      }
    }

    setGridHeight();
    window.addEventListener('resize', setGridHeight);
    // Recalculate once after fonts/images settle
    setTimeout(setGridHeight, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();